import { useState, useCallback } from 'react';

const STORAGE_KEY = 'locale';
const DEFAULT_LOCALE = process.env.REACT_APP_DEFAULT_LOCALE || 'en';

/**
 * useLocale — manages the current locale preference.
 *
 * @param {string[]} [supportedLocales] — list of supported locale codes
 * @returns {{ locale, setLocale, getSavedLocale }}
 */
const useLocale = (supportedLocales = [DEFAULT_LOCALE]) => {
  const getSavedLocale = () => {
    if (typeof localStorage === 'undefined') return DEFAULT_LOCALE;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && supportedLocales.includes(saved)) return saved;
    // Check browser language
    const browserLang = navigator?.language?.split('-')[0];
    if (browserLang && supportedLocales.includes(browserLang)) return browserLang;
    return DEFAULT_LOCALE;
  };

  const [locale, setLocaleState] = useState(getSavedLocale);

  const setLocale = useCallback((newLocale) => {
    if (!supportedLocales.includes(newLocale)) return;
    setLocaleState(newLocale);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newLocale);
    }
  }, [supportedLocales]);

  return { locale, setLocale, getSavedLocale };
};

export default useLocale;
